import React, { useState, useEffect } from "react";
import TableSelector from "./TableSelector";
import FieldSelector from "./FieldSelector";
import JoinExplorer from "./JoinExplorer";
import SelectedFieldsPanel from "./SelectedFieldsPanel";
import { connect } from "react-redux";
import {
  downloadExcelReport,
  getFieldMetadata,
  getJoinsMetadata,
  getQueryPreview,
  getTableMetadata,
} from "../../../store/reporting/actions";
import FieldAliasManager from "./FieldAliasManager";
import QueryConditions from "./QueryConditions";
import ReportPreviewTable from "./ReportPreviewTable";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";

const ReportBuilder = ({
  getTableMetadata,
  getFieldMetadata,
  getJoinsMetadata,
  getQueryPreview,
  downloadExcelReport,
}) => {
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [baseTable, setBaseTable] = useState("");
  const [joinedTables, setJoinedTables] = useState([]);
  const [joins, setJoins] = useState([]);
  const [fieldsMap, setFieldsMap] = useState({});
  const [joinsByTable, setJoinsByTable] = useState({});
  const [selectedFieldsMap, setSelectedFieldsMap] = useState({});
  const [fieldAliases, setFieldAliases] = useState({}); // code → alias
  const [aliasConflicts, setAliasConflicts] = useState([]); // array of conflicting codes
  const [queryOptions, setQueryOptions] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load all table names on mount
  useEffect(() => {
    getTableMetadata((status, data) => {
      setLoading(status);
      setTables(data);
    });
  }, []);

  // Load fields for selected table
  const loadFields = (tableName) => {
    getFieldMetadata(tableName, (status, data) => {
      setLoading(status);
      if (!!data && !status) {
        setFieldsMap((prev) => ({ ...prev, [tableName]: data }));
      }
    });
  };

  // Load joins for selected table
  const loadJoinsForTable = (tableName) => {
    if (!tableName) return;
    getJoinsMetadata(tableName, (status, data) => {
      setLoading(status);
      if (!!data && !status) {
        setJoinsByTable((prev) => ({ ...prev, [tableName]: data }));
      }
    });
  };

  useEffect(() => {
    joinedTables.forEach((table) => {
      if (!joinsByTable[table]) {
        loadJoinsForTable(table);
      }
    });
  }, [joinedTables]);

  // Handle base table selection
  const handleBaseTableSelect = (tableName) => {
    setBaseTable(tableName);
    setJoinedTables([tableName]);
    loadFields(tableName);
  };

  // Handle field selection toggle
  const handleToggleField = (table, code) => {
    setSelectedFieldsMap((prev) => {
      const current = prev[table] || [];
      const alreadySelected = current.find((f) => f.code === code);
      const updated = alreadySelected
        ? current.filter((f) => f.code !== code)
        : [...current, fieldsMap[table].find((f) => f.code === code)];

      return { ...prev, [table]: updated };
    });

    setFieldAliases((prev) => {
      const [moduleName, columnName] = code.split("_").slice(-2);
      const alias = suggestFieldAlias(table, columnName);
      return {
        ...prev,
        [code]: prev[code] || alias,
      };
    });
  };

  // Handle adding a join
  const handleJoinTable = (
    joinToTable,
    fromTable,
    fromColumn,
    toColumn,
    type = "left"
  ) => {
    console.log({
      from: fromTable,
      to: joinToTable,
      fromColumn,
      toColumn,
      type,
    });

    if (joinedTables.includes(joinToTable)) return;

    setJoinedTables((prev) => [...prev, joinToTable]);
    setJoins((prev) => [
      ...prev,
      {
        from: fromTable,
        to: joinToTable,
        fromColumn,
        toColumn,
        type,
      },
    ]);

    loadFields(joinToTable);
  };

  // Handle clearing all selected fields
  const handleClearAll = () => {
    setSelectedFieldsMap({});
  };

  // ✅ Select or Deselect All Fields from a Table
  const handleToggleAllFields = (table, shouldSelectAll) => {
    if (!fieldsMap[table]) return;

    const updatedFields = shouldSelectAll ? [...fieldsMap[table]] : [];

    setSelectedFieldsMap((prev) => ({
      ...prev,
      [table]: updatedFields,
    }));

    if (shouldSelectAll) {
      setFieldAliases((prev) => {
        const updated = { ...prev };
        updatedFields.forEach((f) => {
          if (!updated[f.code]) {
            updated[f.code] = suggestFieldAlias(table, f.column);
          }
        });
        return updated;
      });
    } else {
      setFieldAliases((prev) => {
        const updated = { ...prev };
        fieldsMap[table].forEach((f) => delete updated[f.code]);
        return updated;
      });
    }
  };

  // ✅ Remove a Table from Joined Tables
  const handleRemoveTable = (tableToRemove) => {
    // Prevent removing base table
    if (tableToRemove === baseTable) return;

    setJoinedTables((prev) => prev.filter((t) => t !== tableToRemove));

    setSelectedFieldsMap((prev) => {
      const copy = { ...prev };
      delete copy[tableToRemove];
      return copy;
    });

    setJoinsByTable((prev) => {
      const copy = { ...prev };
      delete copy[tableToRemove];
      return copy;
    });

    setFieldsMap((prev) => {
      const copy = { ...prev };
      delete copy[tableToRemove];
      return copy;
    });
  };

  const suggestFieldAlias = (tableName, columnName) => {
    const tablePart = tableName.split("_")[1] || tableName;
    const cleanedTable = tablePart.replace(/([A-Z])/g, " $1").trim();
    const tableLabel = cleanedTable
      .split(" ")
      .map((word) => word.toLowerCase())
      .join("");

    // Special case: column ends with "id" — suggest clearer identifier
    if (columnName.toLowerCase() === "id") {
      return `${tableLabel}Id`;
    }

    return `${tableLabel}${columnName
      .charAt(0)
      .toUpperCase()}${columnName.slice(1)}`;
  };

  const detectAliasConflicts = (selectedFieldsWithAliases) => {
    const aliasMap = {};
    const conflicts = [];

    selectedFieldsWithAliases.forEach(({ alias, code }) => {
      if (aliasMap[alias]) {
        conflicts.push(code);
        conflicts.push(aliasMap[alias]);
      } else {
        aliasMap[alias] = code;
      }
    });

    return Array.from(new Set(conflicts));
  };

  useEffect(() => {
    const selected = Object.values(selectedFieldsMap).flat() || [];
    const selectedWithAliases = selected.map((f) => ({
      code: f.code,
      alias: fieldAliases[f.code] || "",
    }));

    const conflicts = detectAliasConflicts(selectedWithAliases);
    setAliasConflicts(conflicts);
  }, [selectedFieldsMap, fieldAliases]);

  const handleAliasChange = (code, newAlias) => {
    setFieldAliases((prev) => ({
      ...prev,
      [code]: newAlias,
    }));
  };

  const handlePreviewSample = async () => {
    if (aliasConflicts.length > 0) {
      alert("Please resolve alias conflicts before previewing.");
      return;
    }

    setShowPreview(true);

    const fields = Object.entries(selectedFieldsMap).flatMap(
      ([table, fields]) =>
        fields.map((f) => ({
          code: f.code,
          alias: fieldAliases[f.code] || f.code,
        }))
    );

    getQueryPreview(
      {
        fields,
        joins,
        conditions: queryOptions,
        baseTable,
      },
      (status, data) => {
        setIsPreviewLoading(status);
        setPreviewData(data);
      }
    );
  };

  const handleDownload = () => {
    if (!aliasConflicts || aliasConflicts.length > 0) {
      alert("Please resolve alias conflicts before previewing.");
      return;
    }

    const fields = Object.entries(selectedFieldsMap).flatMap(
      ([table, fields]) =>
        fields.map((f) => ({
          code: f.code,
          alias: fieldAliases[f.code] || f.code,
        }))
    );

    downloadExcelReport(
      {
        fields,
        joins,
        conditions: queryOptions,
        baseTable,
      },
      (status) => {
        setDownloading(status);
      }
    );
  };

  return (
    <div className="p-5">
      <h2 className="mb-4">🛠️ Build Your Report</h2>
      {/* <div className="row"> */}
      {/* <div className="col-7"> */}
      <TableSelector
        tables={tables}
        selectedTable={baseTable}
        onSelectTable={handleBaseTableSelect}
      />

      {joinedTables.map((table) => (
        <FieldSelector
          key={table}
          tableLabel={table
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
          fields={fieldsMap[table] || []}
          table={table}
          selectedFields={selectedFieldsMap[table]?.map((f) => f.code) || []}
          onToggleField={(table, code) => handleToggleField(table, code)}
          onToggleAll={handleToggleAllFields}
          onRemoveTable={handleRemoveTable}
          showRemove={table !== baseTable}
        />
      ))}

      <JoinExplorer
        joinedTables={joinedTables}
        joinsByTable={joinsByTable}
        alreadyJoined={joinedTables}
        onJoinTable={(to, from, fromCol, toCol, joinType) =>
          handleJoinTable(to, from, fromCol, toCol, joinType)
        }
      />

      <SelectedFieldsPanel
        selectedFieldsMap={selectedFieldsMap}
        onClearAll={handleClearAll}
      />

      <FieldAliasManager
        selectedFieldsMap={selectedFieldsMap}
        fieldAliases={fieldAliases}
        aliasConflicts={aliasConflicts}
        onAliasChange={handleAliasChange}
      />

      <QueryConditions
        availableFields={Object.values(selectedFieldsMap).flat()} // flatten selected fields
        onConditionsChange={setQueryOptions}
      />

      <button
        className="btn btn-outline-secondary mr-2"
        disabled={aliasConflicts.length > 0}
        onClick={handlePreviewSample}
      >
        👁️ Preview Sample
      </button>

      <button
        className="btn btn-primary"
        disabled={aliasConflicts.length > 0 || isDownloading}
        onClick={handleDownload}
      >
        Generate Report
      </button>
      {/* </div> */}
      {/* </div> */}

      {!!showPreview && (
        <Dialog
          onClose={() => setShowPreview(false)}
          aria-labelledby="customized-dialog-title"
          open={true}
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle>
            <Typography className="text-primary text-uppercase">
              <span className="d-flex align-items-center">
                <span>Data Preview</span>
              </span>
            </Typography>

            <IconButton
              aria-label="close"
              onClick={() => setShowPreview(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <span className="material-icons">close</span>
            </IconButton>
          </DialogTitle>

          <DialogContent className="mx-4 px-2">
            <ReportPreviewTable data={previewData} loading={isPreviewLoading} />
          </DialogContent>
          <DialogActions className="d-flex justify-content-center py-3"></DialogActions>
        </Dialog>
      )}
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};
export default connect(mapStateToProps, {
  getTableMetadata,
  getFieldMetadata,
  getJoinsMetadata,
  getQueryPreview,
  downloadExcelReport,
})(ReportBuilder);
